import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import IP_URL from '../connect';
import Navbar from '../Navbar';
import Sidebar from '../Sidebar';
import { useSelector } from 'react-redux';
import { selectUser } from '../../Redux/currentUserSlice';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Main = styled.div``;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  align-items: center;
`;

const Container = styled.div`
  margin: 20px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  width: 95%;
  border-radius: 10px;
  background-color: #ffffff;
  animation: ${fadeIn} 0.4s ease-in;
  overflow-x: auto;
`;

const Table = styled.table`
  border-collapse: collapse;
  background-color: #fdfdfd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: 100%;

  th, td {
    padding: 12px 15px;
    border: 1px solid #e0e0e0;
    text-align: center;
    white-space: nowrap;
  }

  th {
    background-color: #1976d2;
    color: white;
    font-weight: bold;
  }

  tbody tr {
    transition: background 0.2s ease;
  }

  tbody tr:nth-child(odd) {
    background-color: #cce5f8ff;
  }

  tbody tr:hover {
    background-color: #e3f2fd;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    th, td {
      padding: 6px 8px;
    }
  }
`;

const LoadingSpinner = styled.div`
  margin: 40px auto;
  font-size: 20px;
  color: #1976d2;
  text-align: center;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px auto;

  button {
    margin: 0 10px;
    padding: 8px 14px;
    border: none;
    border-radius: 6px;
    background-color: #1976d2;
    color: white;
    font-weight: bold;
    transition: 0.3s ease;
    cursor: pointer;

    &:hover {
      background-color: #004ba0;
    }

    &:disabled {
      background-color: #aaa;
      cursor: not-allowed;
    }
  }
`;

const Input = styled.input`
  padding: 8px;
  margin: 5px;
  border-radius: 5px;
  border: 1px solid #ccc;
  outline: none;
  transition: border 0.2s ease;

  &:focus {
    border-color: #1976d2;
  }
`;

const AdminMeterData = () => {
  const { token } = useSelector(selectUser);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  // search
  const [serialNo, setSerialNo] = useState('');
  const [imei, setImei] = useState('');
  const [date, setDate] = useState('');

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  useEffect(() => {
    const getOriginalData = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          page,
          limit,
          ...(imei && { imei }),
          ...(serialNo && { serialNo }),
          ...(date && { date }),
        });

        const response = await fetch(`${IP_URL}/currentData/original-data?${queryParams.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const result = await response.json();

        if (!result.data || result.data.length === 0) {
          toast.info('No data found for your search!', { position: 'top-center' });
        } else {
          toast.success('Data loaded successfully!', { position: 'bottom-right' });
        }

        setOriginalData(result.data || []);
        setTotalPages(result.totalPages || 1);
      } catch (error) {
        console.error('Error fetching original data:', error);
        toast.error('Error fetching meter data. Please try again.', { position: 'top-right' });
      } finally {
        setLoading(false);
      }
    };

    getOriginalData();
  }, [page, limit, token, imei, serialNo, date]);

  const navigateToMeterDetails = (imei) => navigate(`/admin-meter-details/${imei}`);
  return (
    <Main>
      <Navbar showMenuButton={true} toggleSidebar={toggleSidebar} />
      
      <Content>
        <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Container>
          {loading && <LoadingSpinner>Loading...</LoadingSpinner>}

          {!loading && (
            <>
              <Table>
                <thead>
                  <tr>
                    <th>
                      <Input
                        type="text"
                        placeholder="Search Serial"
                        value={serialNo}
                        onChange={(e) => setSerialNo(e.target.value)}
                      />
                    </th>
                    <th>
                      <Input
                        type="text"
                        placeholder="Search IMEI"
                        value={imei}
                        onChange={(e) => setImei(e.target.value)}
                      />
                    </th>
                    <th colSpan="8">
                      <Input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>Device ID</th>
                    <th>IMEI</th>
                    <th>Meter Reading (m³)</th>
                    <th>Balance (m³)</th>
                    <th>Battery (mV)</th>
                    <th>Flow Rate</th>
                    <th>Cumulative Flow</th>
                    <th>Valve Status</th>
                    <th>SNR</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {originalData.map((item, index) => (
                    <tr key={index}  onClick={() => navigateToMeterDetails(item.imei )}>
                      <td>{item.deviceId}</td>
                      <td>{item.imei}</td>
                      <td>{(item.meterReading / 1000).toFixed(2)} m³</td>
                      <td>
                        {item.availableWaterBalance != null
                          ? (item.availableWaterBalance / 1000).toFixed(2) + ' m³'
                          : '-'}
                      </td>
                      <td>{item.powerVoltage}</td>
                      <td>{item.realFlowRate}</td>
                      <td>{(item.positiveCumulativeFlow / 1000).toFixed(2)} m³</td>
                      <td>
                        {item.valveControlStatus === 0 && <span style={{ color: 'green' }}>Open</span>}
                        {item.valveControlStatus === 1 && <span style={{ color: 'red' }}>Closed</span>}
                        {item.valveControlStatus === 2 && <span style={{ color: 'gray' }}>No Valve</span>}
                        {item.valveControlStatus === null && <span style={{ color: 'gray' }}>No Valve</span>}
                      </td>
                      <td>{item.snr}</td>
                      <td>{new Date(item.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Pagination>
                <button
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((prev) => prev - 1);
                    toast.info('Previous page loaded', { position: 'bottom-left' });
                  }}
                >
                  Previous
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => {
                    setPage((prev) => prev + 1);
                    toast.info('Next page loaded', { position: 'bottom-left' });
                  }}
                >
                  Next
                </button>
              </Pagination>
            </>
          )}
        </Container>
      </Content>
    </Main>
  );
};

export default AdminMeterData;
